import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { CalendarClock, X, RefreshCw, FileSpreadsheet, ChevronDown, ChevronRight, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin-only panel showing every future-dated absent/late mark created via
 * the quick-attendance widget (parent called ahead about a vacation week).
 * Admin can cancel one before the day rolls around if the parent calls
 * back saying "actually they'll come."
 */
export default function PlannedAbsencesPanel() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [today, setToday] = useState("");
  const [edCount, setEdCount] = useState(0);
  const [edItems, setEdItems] = useState([]);
  const [edError, setEdError] = useState(false);
  const [edExpanded, setEdExpanded] = useState(false);
  const [upItems, setUpItems] = useState([]);
  const [upRecurring, setUpRecurring] = useState([]);
  const [upExpanded, setUpExpanded] = useState(false);
  const [showAddRecurring, setShowAddRecurring] = useState(false);
  const [recForm, setRecForm] = useState({ camper_name: "", group_code: "", pickup_time: "", end_date: "" });
  const [savingRecurring, setSavingRecurring] = useState(false);
  const [downloadingXlsx, setDownloadingXlsx] = useState(false);
  const [restoringCancelled, setRestoringCancelled] = useState(false);

  const loadUpcomingDismissals = async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/admin/early-dismissals/upcoming`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUpItems(res.data?.upcoming || []);
      setUpRecurring(res.data?.recurring || []);
    } catch (_e) { /* silent */ }
  };

  const addRecurringDismissal = async () => {
    if (!recForm.camper_name.trim() || !recForm.group_code.trim() || !recForm.pickup_time.trim()) {
      toast.error("Camper name, group, and pickup time are required");
      return;
    }
    setSavingRecurring(true);
    try {
      const token = localStorage.getItem("token");
      const body = { ...recForm };
      if (!body.end_date) delete body.end_date;
      await axios.post(`${API}/admin/permanent-dismissals`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`${recForm.camper_name} added — daily pickup at ${recForm.pickup_time}`);
      setRecForm({ camper_name: "", group_code: "", pickup_time: "", end_date: "" });
      setShowAddRecurring(false);
      loadUpcomingDismissals();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to add");
    }
    setSavingRecurring(false);
  };

  const removeRecurringDismissal = async (recId, name) => {
    if (!window.confirm(`Stop the recurring daily dismissal for ${name}?`)) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API}/admin/permanent-dismissals/${recId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Removed ${name}'s recurring dismissal`);
      loadUpcomingDismissals();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove");
    }
  };

  const cancelUpcomingDismissal = async (notifId, name, date) => {
    if (!window.confirm(`Cancel the planned early dismissal for ${name} on ${date}?`)) return;
    try {
      const token = localStorage.getItem("token");
      await axios.patch(`${API}/admin/early-dismissals/${notifId}/cancel`, null, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`Cancelled ${name}'s dismissal on ${date}`);
      loadUpcomingDismissals();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cancel");
    }
  };

  const loadEarlyDismissalsCount = async (dateStr) => {
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/admin/early-dismissals/today`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { date: dateStr, ts: Date.now() },
      });
      setEdCount(res.data?.count || 0);
      setEdItems(res.data?.early_dismissals || []);
      setEdError(false);
    } catch (e) {
      console.error("Failed to load today's early dismissals:", e);
      setEdError(true);
    }
  };

  const removeTodayDismissal = async (ed) => {
    const label = ed.source === "recurring" ? "recurring dismissal" : "dismissal";
    if (!window.confirm(`Remove ${ed.camper_name}'s ${label} from today's list? This deletes it at the source.`)) return;
    try {
      const token = localStorage.getItem("token");
      const dateStr = today || new Date().toISOString().slice(0, 10);
      await axios.post(`${API}/admin/early-dismissals/remove-today`, {
        id: ed.id,
        source: ed.source || "",
        camper_id: ed.camper_id || "",
        camper_name: ed.camper_name || "",
        group_code: ed.group_code || "",
        date: dateStr,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Removed ${ed.camper_name}'s ${label}`);
      loadEarlyDismissalsCount(dateStr);
      loadUpcomingDismissals();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to remove");
    }
  };

  const restoreCancelledDismissals = async () => {
    const dateStr = today || new Date().toISOString().slice(0, 10);
    if (!window.confirm(`Restore ALL cancelled early dismissals for ${dateStr}? This re-opens rows that were wiped (one per camper). Signed-out campers are skipped; remove any that shouldn't return with the red X.`)) return;
    setRestoringCancelled(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(`${API}/admin/early-dismissals/restore-cancelled`,
        { date: dateStr }, { headers: { Authorization: `Bearer ${token}` } });
      const names = (res.data?.restored || []).map(r => r.camper_name).join(", ");
      toast.success(res.data?.restored_count
        ? `Restored ${res.data.restored_count}: ${names}`
        : "Nothing to restore — no cancelled dismissals found for this date.");
      loadEarlyDismissalsCount(dateStr);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to restore");
    } finally {
      setRestoringCancelled(false);
    }
  };

  const downloadEarlyDismissalsXlsx = async () => {
    setDownloadingXlsx(true);
    try {
      const token = localStorage.getItem("token");
      const dateStr = today || new Date().toISOString().slice(0, 10);
      const res = await axios.get(`${API}/admin/early-dismissals/today.xlsx`, {
        responseType: "blob",
        params: { date: dateStr, ts: Date.now() },
        headers: { Authorization: `Bearer ${token}` },
      });
      const url = URL.createObjectURL(new Blob([res.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `Early_Dismissals_${dateStr}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded — ${edCount} dismissals for ${dateStr}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to download Excel file");
    } finally {
      setDownloadingXlsx(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/admin/planned-absences`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(res.data.items || []);
      const t = res.data.today || new Date().toISOString().slice(0, 10);
      setToday(t);
      loadEarlyDismissalsCount(t);
      loadUpcomingDismissals();
    } catch (e) {
      // silent — admins may not need this panel every day
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const cancel = async (camperId, date, name) => {
    if (!window.confirm(`Cancel planned absence for ${name} on ${date}?`)) return;
    try {
      const token = localStorage.getItem("token");
      await axios.delete(
        `${API}/admin/planned-absences?camper_id=${encodeURIComponent(camperId)}&date=${encodeURIComponent(date)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Cleared ${name} on ${date}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cancel");
    }
  };

  // Group by date for a cleaner display
  const byDate = {};
  for (const it of items) {
    const d = it.date || "";
    if (!byDate[d]) byDate[d] = [];
    byDate[d].push(it);
  }
  const sortedDates = Object.keys(byDate).sort();

  return (
    <Card className="border-blue-200" data-testid="planned-absences-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm flex items-center justify-between text-blue-800">
          <span className="flex items-center gap-2">
            <CalendarClock className="w-4 h-4" />
            Upcoming Planned Absences
            <span className="text-[10px] bg-blue-100 text-blue-700 rounded-full px-2 py-0.5 font-bold">
              {items.length}
            </span>
          </span>
          <button onClick={load} title="Refresh" className="text-blue-600 hover:text-blue-800"
            data-testid="planned-absences-refresh">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
          </button>
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-0">
        {loading && items.length === 0 ? (
          <p className="text-xs text-gray-500 italic">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-xs text-gray-500">
            No upcoming planned absences. When a parent calls about future dates, those will appear here.
          </p>
        ) : (          <div className="space-y-2 max-h-72 overflow-y-auto">
            {sortedDates.map(date => (
              <div key={date} className="border border-blue-100 rounded-lg p-2 bg-blue-50/40"
                data-testid={`planned-absences-date-${date}`}>
                <div className="text-xs font-bold text-blue-900 mb-1">{date}</div>
                <div className="space-y-1">
                  {byDate[date].map(it => {
                    // Show ORIGINAL planned status (absent/late) even if the
                    // attendance app later flipped `status` to "present".
                    const displayStatus = it.planned_status || it.status;
                    const wasOverridden = it.status === "present" && displayStatus !== "present";
                    return (
                      <div key={`${it.camper_id}-${it.date}`}
                        className="flex items-center justify-between bg-white border border-blue-100 rounded px-2 py-1">
                        <div className="text-xs">
                          <span className="font-semibold text-gray-900">{it.camper_name}</span>
                          <span className="text-gray-500 ml-1">({it.group_code})</span>
                          <span className={`ml-2 inline-block text-[9px] font-bold uppercase px-1.5 py-0.5 rounded ${
                            displayStatus === "absent" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"
                          }`}>
                            {displayStatus}
                          </span>
                          {wasOverridden && (
                            <span className="ml-1 inline-block text-[9px] font-semibold uppercase px-1.5 py-0.5 rounded bg-green-100 text-green-700"
                                  title="Kid actually showed up — overridden by Attendance app">
                              showed up
                            </span>
                          )}
                        </div>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 px-2 text-red-600 hover:bg-red-50 hover:text-red-800"
                          onClick={() => cancel(it.camper_id, it.date, it.camper_name)}
                          data-testid={`planned-absences-cancel-${it.camper_id}-${it.date}`}
                        >
                          <X className="w-3 h-3 mr-1" /> Cancel
                        </Button>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Upcoming (future-dated) + recurring early dismissals — so the office
            can SEE what's already entered and stops re-entering it */}
        <div className="mt-3 pt-3 border-t border-blue-100">
          <button
            type="button"
            onClick={() => setUpExpanded(v => !v)}
            className="w-full flex items-center justify-between text-xs font-semibold text-violet-800 hover:text-violet-900 mb-2"
            data-testid="planned-absences-toggle-upcoming-ed"
          >
            <span className="flex items-center gap-1.5">
              {upExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              <CalendarClock className="w-3.5 h-3.5" />
              Planned Early Dismissals (future + recurring)
              <span className="text-[10px] bg-violet-100 text-violet-700 rounded-full px-2 py-0.5 font-bold">
                {upItems.length + upRecurring.length}
              </span>
            </span>
          </button>

          {upExpanded && (
            <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="planned-absences-upcoming-ed-list">
              <div className="border border-violet-100 rounded-lg p-2 bg-violet-50/40">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] font-bold uppercase text-violet-800">Recurring — every camp day</div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-6 px-2 text-[10px] border-violet-300 text-violet-700 hover:bg-violet-100"
                    onClick={() => setShowAddRecurring(v => !v)}
                    data-testid="add-recurring-dismissal-toggle"
                  >
                    {showAddRecurring ? "Close" : "+ Add Recurring"}
                  </Button>
                </div>
                {showAddRecurring && (
                  <div className="mt-2 grid grid-cols-2 gap-1.5 bg-white border border-violet-200 rounded p-2" data-testid="add-recurring-dismissal-form">
                    <input
                      className="col-span-2 text-xs border rounded px-2 py-1"
                      placeholder="Camper name (First Last)"
                      value={recForm.camper_name}
                      onChange={e => setRecForm(f => ({ ...f, camper_name: e.target.value }))}
                      data-testid="recurring-camper-name-input"
                    />
                    <input
                      className="text-xs border rounded px-2 py-1"
                      placeholder="Group (e.g. B08)"
                      value={recForm.group_code}
                      onChange={e => setRecForm(f => ({ ...f, group_code: e.target.value.toUpperCase() }))}
                      data-testid="recurring-group-input"
                    />
                    <input
                      className="text-xs border rounded px-2 py-1"
                      placeholder="Pickup time (1:00 PM)"
                      value={recForm.pickup_time}
                      onChange={e => setRecForm(f => ({ ...f, pickup_time: e.target.value }))}
                      data-testid="recurring-time-input"
                    />
                    <div className="col-span-2 flex items-center gap-1.5">
                      <label className="text-[10px] text-gray-500 whitespace-nowrap">Through:</label>
                      <input
                        type="date"
                        className="text-xs border rounded px-2 py-1 flex-1"
                        value={recForm.end_date}
                        onChange={e => setRecForm(f => ({ ...f, end_date: e.target.value }))}
                        data-testid="recurring-end-date-input"
                      />
                      <span className="text-[10px] text-gray-400">blank = all summer</span>
                    </div>
                    <Button
                      size="sm"
                      className="col-span-2 h-7 text-xs bg-violet-600 hover:bg-violet-700 text-white"
                      disabled={savingRecurring}
                      onClick={addRecurringDismissal}
                      data-testid="recurring-save-btn"
                    >
                      {savingRecurring ? "Saving..." : "Save — repeats every camp day"}
                    </Button>
                  </div>
                )}
                {upRecurring.length === 0 && !showAddRecurring && (
                  <p className="text-[10px] text-gray-500 italic mt-1">None yet — add your everyday-pickup kids once and they'll show here + get daily reminders.</p>
                )}
                <div className="space-y-1 mt-1.5">
                  {upRecurring.map(r => (
                    <div key={r.id} className="flex items-center justify-between bg-white border border-violet-100 rounded px-2 py-1"
                      data-testid={`upcoming-ed-recurring-${r.id}`}>
                      <div className="text-xs">
                        <span className="font-semibold text-gray-900">{r.camper_name}</span>
                        <span className="text-gray-500 ml-1">({r.group_code})</span>
                        <span className="text-[10px] text-gray-500 ml-1">· daily through {r.end_date}</span>
                        {r.is_flex && (
                          <span className="text-[9px] font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded px-1 py-0.5 ml-1" data-testid={`recurring-flex-badge-${r.id}`}>
                            FLEX — only their days
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] font-bold text-violet-700 bg-white border border-violet-200 rounded px-1.5 py-0.5">
                          {r.pickup_time || "—"}
                        </span>
                        <button
                          type="button"
                          className="text-red-500 hover:text-red-700 p-0.5"
                          onClick={() => removeRecurringDismissal(r.id, r.camper_name)}
                          data-testid={`recurring-remove-${r.id}`}
                        >
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {upItems.length === 0 && upRecurring.length === 0 ? (
                <p className="text-xs text-gray-500 italic px-1">No future early dismissals logged yet.</p>
              ) : (
                Object.entries(
                  upItems.reduce((acc, ed) => {
                    const d = ed.event_date || "unknown";
                    (acc[d] = acc[d] || []).push(ed);
                    return acc;
                  }, {})
                ).sort(([a], [b]) => a.localeCompare(b)).map(([date, list]) => (
                  <div key={date} className="border border-violet-100 rounded-lg p-2 bg-violet-50/40"
                    data-testid={`upcoming-ed-date-${date}`}>
                    <div className="text-xs font-bold text-violet-900 mb-1">{date}</div>
                    <div className="space-y-1">
                      {list.map(ed => (
                        <div key={ed.id} className="flex items-center justify-between bg-white border border-violet-100 rounded px-2 py-1"
                          data-testid={`upcoming-ed-row-${ed.id}`}>
                          <div className="text-xs">
                            <span className="font-semibold text-gray-900">{ed.camper_name}</span>
                            <span className="text-gray-500 ml-1">({ed.group_code})</span>
                            {ed.pickup_by && <span className="text-[10px] text-gray-500 ml-1">· {ed.pickup_by}</span>}
                            <span className="text-[10px] font-bold text-violet-700 bg-violet-50 border border-violet-200 rounded px-1.5 py-0.5 ml-2">
                              {ed.pickup_time || ed.dismissal_time || "—"}
                            </span>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 px-2 text-red-600 hover:bg-red-50 hover:text-red-800"
                            onClick={() => cancelUpcomingDismissal(ed.id, ed.camper_name, ed.event_date)}
                            data-testid={`upcoming-ed-cancel-${ed.id}`}
                          >
                            <X className="w-3 h-3 mr-1" /> Cancel
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </div>

        {/* Today's Running Early Dismissals — collapsible list + Excel export */}
        <div className="mt-3 pt-3 border-t border-blue-100">
          <button
            type="button"
            onClick={() => setEdExpanded(v => !v)}
            className="w-full flex items-center justify-between text-xs font-semibold text-emerald-800 hover:text-emerald-900 mb-2"
            data-testid="planned-absences-toggle-ed-list"
          >
            <span className="flex items-center gap-1.5">
              {edExpanded ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
              <Clock className="w-3.5 h-3.5" />
              Today&apos;s Early Dismissals
              <span className="text-[10px] bg-emerald-100 text-emerald-700 rounded-full px-2 py-0.5 font-bold">
                {edCount}
              </span>
            </span>
          </button>

          {edExpanded && (
            <div className="space-y-1 max-h-60 overflow-y-auto mb-2" data-testid="planned-absences-ed-list">
              {edError ? (
                <p className="text-xs text-red-600 italic px-1" data-testid="planned-absences-ed-error">Couldn&apos;t load today&apos;s dismissals — refresh to retry.</p>
              ) : edItems.length === 0 ? (
                <p className="text-xs text-gray-500 italic px-1">No early dismissals logged for today yet.</p>
              ) : (
                edItems.map(ed => (
                  <div
                    key={ed.id}
                    className={`flex items-center justify-between rounded px-2 py-1 border ${ed.dismissed ? "bg-gray-100 border-gray-200 opacity-70" : "bg-emerald-50/60 border-emerald-100"}`}
                    data-testid={`planned-absences-ed-row-${ed.id}`}
                  >
                    <div className="text-xs">
                      <span className={`font-semibold ${ed.dismissed ? "text-gray-500 line-through" : "text-gray-900"}`}>{ed.camper_name}</span>
                      <span className="text-gray-500 ml-1">({ed.group_code})</span>
                      {ed.pickup_by && (
                        <span className="text-[10px] text-gray-500 ml-1">· {ed.pickup_by}</span>
                      )}
                      {ed.dismissed && ed.cancelled_label && (
                        <span className="block text-[10px] font-semibold text-rose-600" data-testid={`planned-absences-ed-cancel-label-${ed.id}`}>
                          {ed.cancelled_label}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className={`text-[10px] font-bold rounded px-1.5 py-0.5 border ${ed.dismissed ? "text-gray-500 bg-white border-gray-200 line-through" : "text-emerald-700 bg-white border-emerald-200"}`}>
                        {ed.dismissal_time || "—"}
                      </span>
                      <button
                        onClick={() => removeTodayDismissal(ed)}
                        className="text-red-400 hover:text-red-600 hover:bg-red-50 rounded p-0.5"
                        title={`Remove ${ed.camper_name}'s dismissal`}
                        data-testid={`planned-absences-ed-remove-${ed.id}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}

          <Button
            size="sm"
            variant="outline"
            className="w-full text-xs border-emerald-300 text-emerald-700 hover:bg-emerald-50"
            onClick={downloadEarlyDismissalsXlsx}
            disabled={downloadingXlsx}
            data-testid="planned-absences-download-ed-xlsx"
            title="Download today's running early-dismissal list as Excel"
          >
            <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" />
            {downloadingXlsx
              ? "Generating…"
              : `Download Excel (${edCount})`}
          </Button>

          <Button
            size="sm"
            variant="outline"
            className="w-full mt-1.5 text-xs border-amber-300 text-amber-700 hover:bg-amber-50"
            onClick={restoreCancelledDismissals}
            disabled={restoringCancelled}
            data-testid="planned-absences-restore-cancelled"
            title="Re-open today's cancelled early dismissals (undo an accidental wipe)"
          >
            <Clock className="w-3.5 h-3.5 mr-1.5" />
            {restoringCancelled ? "Restoring…" : "Restore cancelled dismissals"}
          </Button>
        </div>

        <WeeklyDismissalScheduleTool />
      </CardContent>
    </Card>
  );
}

const WEEKLY_DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri"];
const WEEKLY_LOCATIONS = ["Main Tent", "Hockey Rink", "Auditorium", "After Care", "Front Door"];

function WeeklyDismissalScheduleTool() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [schedule, setSchedule] = useState({});
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${localStorage.getItem("token")}` };

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2 || selected) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const tokens = q.split(/\s+/).filter(Boolean);
        const res = await axios.get(`${API}/campers`, { headers, params: { search: tokens[0] } });
        const all = res.data || [];
        const matches = all.filter((c) => {
          const full = `${c.first_name || ""} ${c.last_name || ""} ${c.preferred_name || ""}`.toLowerCase();
          return tokens.every((tok) => full.includes(tok.toLowerCase()));
        });
        setResults(matches.slice(0, 8));
      } catch { setResults([]); }
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, selected]);

  const pick = async (camper) => {
    setSelected(camper);
    setQuery(`${camper.first_name} ${camper.last_name} (${camper.group_code})`);
    setResults([]);
    try {
      const res = await axios.get(`${API}/admin/campers/${camper.id}/weekly-dismissal`, { headers });
      setSchedule(res.data.schedule || {});
    } catch { setSchedule({}); }
  };

  const reset = () => { setSelected(null); setQuery(""); setSchedule({}); };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      const res = await axios.put(`${API}/admin/campers/${selected.id}/weekly-dismissal`,
        { schedule }, { headers });
      const n = Object.keys(res.data.schedule || {}).length;
      toast.success(n ? `Weekly schedule saved (${n} day${n !== 1 ? "s" : ""})` : "Weekly schedule cleared");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save schedule");
    } finally { setSaving(false); }
  };

  const clearAll = async () => {
    if (!selected) return;
    if (!window.confirm(`Clear ${selected.first_name}'s weekly dismissal schedule? They go back to normal list logic every day.`)) return;
    try {
      await axios.delete(`${API}/admin/campers/${selected.id}/weekly-dismissal`, { headers });
      setSchedule({});
      toast.success("Weekly schedule cleared");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to clear");
    }
  };

  return (
    <div className="mt-4 pt-3 border-t border-emerald-100" data-testid="weekly-dismissal-tool">
      <div className="flex items-center gap-1.5 mb-2">
        <CalendarClock className="w-4 h-4 text-emerald-700" />
        <span className="text-sm font-bold text-emerald-800">Weekly Dismissal Schedule</span>
        <span className="text-[10px] text-muted-foreground">recurring per-day pickup location · After Care/Front Door = on no list that day · one-off exceptions still win</span>
      </div>
      <div className="relative">
        <input
          value={query}
          onChange={(e) => { setQuery(e.target.value); if (selected) setSelected(null); }}
          placeholder="Type camper name to search…"
          className="w-full px-2.5 py-1.5 text-sm border border-emerald-200 rounded-md bg-white"
          data-testid="weekly-dismissal-search"
          autoComplete="off"
        />
        {results.length > 0 && (
          <div className="absolute z-20 mt-1 w-full bg-white border border-emerald-200 rounded-md shadow-lg max-h-52 overflow-auto">
            {results.map((c) => (
              <button
                key={c.id}
                onClick={() => pick(c)}
                className="w-full text-left px-2.5 py-1.5 text-sm hover:bg-emerald-50"
                data-testid={`weekly-dismissal-result-${c.id}`}
              >
                {c.first_name} {c.last_name} <span className="text-xs text-muted-foreground">({c.group_code})</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {selected && (
        <div className="mt-2 space-y-2">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5">
            {WEEKLY_DAYS.map((day) => (
              <div key={day} className="bg-emerald-50 border border-emerald-100 rounded-md p-1.5">
                <p className="text-[10px] font-bold text-emerald-800 mb-0.5">{day}</p>
                <select
                  value={schedule[day] || ""}
                  onChange={(e) => setSchedule((s) => {
                    const next = { ...s };
                    if (e.target.value) next[day] = e.target.value; else delete next[day];
                    return next;
                  })}
                  className="w-full text-xs border border-emerald-200 rounded bg-white px-1 py-1"
                  data-testid={`weekly-dismissal-day-${day}`}
                >
                  <option value="">— normal —</option>
                  {WEEKLY_LOCATIONS.map((l) => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Button size="sm" onClick={save} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="weekly-dismissal-save-btn">
              {saving ? "Saving…" : "Save Schedule"}
            </Button>
            <Button size="sm" variant="outline" onClick={clearAll} className="border-red-200 text-red-600 hover:bg-red-50" data-testid="weekly-dismissal-clear-btn">
              Clear Schedule
            </Button>
            <Button size="sm" variant="ghost" onClick={reset} data-testid="weekly-dismissal-reset-btn">
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
